import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSyncRuns, stopSyncRun, type SyncRun } from '../lib/api';
import { useDataFreshnessStore, type DataDomain } from '../lib/stores';

const KIND_ICON: Record<string, string> = {
  discourse: '💬', commits: '⎇', release: '🚀', 'azure-devops': '🔷', knowledge: '📚',
  zoho: '🎫', documents: '📄', 'zoho-classify': '🏷', 'zoho-topics': '🧭', 'zoho-embed': '🧬',
};

// When a sync of `kind` finishes with real changes: refetch these list query-key
// prefixes live, and (if `domain` is set) flag that domain's detail pages stale.
const KIND_MAP: Record<string, { lists: string[]; domain?: DataDomain }> = {
  discourse:          { lists: ['discourse-topics', 'discourse-facets', 'discourse-sync-status', 'discourse-insights'], domain: 'discourse' },
  'azure-devops':     { lists: ['azure-work-items', 'azure-facets', 'azure-sync-status'], domain: 'azure' },
  zoho:               { lists: ['zoho-tickets', 'zoho-facets', 'zoho-insights', 'zoho-topics', 'zoho-agent-stats'], domain: 'zoho' },
  commits:            { lists: ['commits'], domain: 'commits' },
  knowledge:          { lists: ['knowledge-status', 'embed-activity'] },
  documents:          { lists: ['documents'] },
  release:            { lists: ['release-status', 'release-sessions', 'tracked-releases', 'release-metrics'] },
  entitlements:       { lists: ['entitlements', 'entitlement-suggestions', 'release-trains'] },
  'zoho-classify':    { lists: ['zoho-tickets', 'zoho-facets', 'zoho-insights'], domain: 'zoho' },
  'zoho-topics':      { lists: ['zoho-topics', 'zoho-insights', 'zoho-tickets', 'zoho-facets'] },
  'zoho-sentiment':   { lists: ['zoho-tickets', 'zoho-insights', 'zoho-facets'], domain: 'zoho' },
  'zoho-embed':       { lists: ['zoho-tickets', 'zoho-insights'] },
  'discourse-import': { lists: ['discourse-topics', 'discourse-facets', 'discourse-sync-status'] },
  'discourse-embed':  { lists: ['discourse-topics', 'discourse-insights'] },
};

// Stat fields whose >0 value means a completed run actually changed data. Used to
// avoid refetching open lists on the frequent no-op autosync cycles.
const CHANGE_FIELDS: Record<string, string[]> = {
  discourse: ['changed', 'failed'], 'azure-devops': ['upserted', 'failed'],
  commits: ['totalSynced', 'ciUpdated'], zoho: ['upserted', 'changed', 'threadsFetched'],
  'zoho-classify': ['classified'], 'zoho-topics': ['topics'], 'zoho-sentiment': ['analyzed'], 'zoho-embed': ['embedded'],
};

const TERMINAL = new Set<SyncRun['status']>(['completed', 'partial', 'stopped', 'interrupted', 'failed']);

/** Whether a finished run changed data worth refetching for. Completed runs are
 *  gated on their stats; other terminals (partial/failed/stopped/interrupted) and
 *  unknown stat shapes default to refetch. 'rejected' never reaches here. */
function shouldInvalidate(run: SyncRun): boolean {
  if (run.status !== 'completed') return true;
  const fields = CHANGE_FIELDS[run.kind];
  if (!fields || !run.stats) return true;
  return fields.some((f) => (run.stats?.[f] ?? 0) > 0);
}

/**
 * Floating toast that appears ONLY while a sync is running, and disappears as
 * soon as it finishes. The full history lives on the Settings page.
 */
export function SyncLogPanel() {
  const queryClient = useQueryClient();

  const { data: runs } = useQuery({
    queryKey: ['sync-runs', 'all', 'def'],
    queryFn: () => getSyncRuns(),
    // Sync state changes arrive via push ('sync' events → RealtimeBridge
    // invalidates ['sync-runs']). Keep only a relaxed while-running poll to animate
    // the progress number; no idle poll — the run-started event wakes this.
    refetchInterval: (query) => {
      const data = query.state.data as SyncRun[] | undefined;
      return data?.some((r) => r.status === 'running') ? 5000 : false;
    },
  });

  // Central post-sync cache refresh. When a tracked run reaches a terminal state
  // with real changes, refetch the affected lists live + the dashboard badges,
  // and flag the matching detail-page domain stale (banner, not auto-refetch).
  // Covers manual AND background autosync, since both produce the same runs.
  const bump = useDataFreshnessStore((s) => s.bump);
  const azureReorderActive = useDataFreshnessStore((s) => s.azureReorderActive);
  const prevStatusRef = useRef<Map<string, SyncRun['status']> | null>(null);
  const pendingListsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!runs) return;

    // Flush list refetches deferred while the user was dragging Backlog rows.
    if (!azureReorderActive && pendingListsRef.current.size > 0) {
      for (const key of pendingListsRef.current) queryClient.invalidateQueries({ queryKey: [key] });
      pendingListsRef.current.clear();
    }

    const prev = prevStatusRef.current;
    // First observation: seed silently so historical terminal runs (on page load)
    // don't trigger an invalidation storm.
    if (prev === null) {
      prevStatusRef.current = new Map(runs.map((r) => [r.id, r.status]));
      return;
    }

    const lists = new Set<string>();
    const domains = new Set<DataDomain>();
    let anyTerminal = false;

    for (const r of runs) {
      const before = prev.get(r.id);
      // running→terminal, or a fast sync that finished between polls (unseen→terminal).
      const justFinished = TERMINAL.has(r.status) && (before === 'running' || before === undefined);
      if (!justFinished) continue;
      anyTerminal = true;
      if (!shouldInvalidate(r)) continue;
      const m = KIND_MAP[r.kind];
      if (m) {
        m.lists.forEach((k) => lists.add(k));
        if (m.domain) domains.add(m.domain);
      }
    }

    if (anyTerminal) queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    for (const key of lists) {
      // Defer the Backlog list refetch while a drag-reorder is in progress.
      if (key === 'azure-work-items' && azureReorderActive) { pendingListsRef.current.add(key); continue; }
      queryClient.invalidateQueries({ queryKey: [key] });
    }
    for (const d of domains) bump(d);

    prevStatusRef.current = new Map(runs.map((r) => [r.id, r.status]));
  }, [runs, azureReorderActive, queryClient, bump]);

  const running = (runs ?? []).filter((r) => r.status === 'running');
  if (running.length === 0) return null;

  const handleStop = async (id: string) => {
    try {
      await stopSyncRun(id);
      queryClient.invalidateQueries({ queryKey: ['sync-runs'] });
    } catch { /* ignore */ }
  };

  return (
    <div className="sync-log-panel">
      <div style={{
        background: 'var(--bg-elevated)', backdropFilter: 'blur(16px)', borderRadius: 12,
        border: '1px solid var(--border-subtle)', boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
        padding: 8, display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        {running.map((r) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', color: 'var(--text-primary)' }}>
            <span style={{ animation: 'pulse 1.5s infinite', fontSize: 14 }}>🔄</span>
            <span>{KIND_ICON[r.kind] || '•'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{r.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: "'SF Mono','Cascadia Code',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.message}
              </div>
            </div>
            <button
              onClick={() => handleStop(r.id)}
              style={{ background: 'transparent', border: '1px solid var(--accent-error)', color: 'var(--accent-error)', borderRadius: 5, fontSize: 11, padding: '3px 10px', cursor: 'pointer', flexShrink: 0 }}
            >
              Stop
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
